import React from 'react';

import { RiskAversion, Survey } from 'Containers/ProfilePage/types';
import { InsuranceType } from 'Containers/ViewBenefitPage/types';
import { ContentfulFbsBenefit } from 'ContentfulDefaults/types/benefits';
import { ContentfulSupplementalPlan } from 'ContentfulDefaults/types/benefitsAdditional';
import { useTextContext } from 'Contexts/textContext';
import { H4, H6, Icon, Tooltip } from 'DesignLibrary/atoms';
import { SupplementalIcon } from 'Shared/SupplementalIcon';
import Text from 'Shared/Text';
import { CoveredMembers, ReasonsWhy, SupplementalPlan } from 'Types/entities';
import { BuilderGoSettings, CdnPlanInfo } from 'Utils/apiTypes';

import { CONTENT_MAP, NO_PLAN_TITLE_OVERRIDE_TYPES } from './constants';
import PlanContent from './PlanContent';
import SelectedPlanCard from './SelectedPlanCard';
import { Container, Header, RecommendedBadge } from './styled';

export interface SupplementalCardProps {
  client: string;
  employerName: string;
  insuranceType: InsuranceType;
  contentfulSupplementalPlans?: Record<string, ContentfulSupplementalPlan> | Record<string, never>;
  selectedPlan?: SupplementalPlan | null;
  coveredMembers?: CoveredMembers;
  // Only affects fsa/fsa_medical cards; ignored for every other insurance type.
  isLimitedPurposeFsa?: boolean;
  supplementalPlans?: SupplementalPlan[];
  recommendedLabel?: string | null;
  isEducational?: boolean;
  reasonsWhy: ReasonsWhy;
  selectedHealthDeductible: number;
  dependentsCount: number;
  surveyAnswers: Survey;
  riskAversion: RiskAversion;
  contentfulPlan?: ContentfulFbsBenefit;
  fbsBestMatch?: boolean;
  builderCustomerKey: string | null;
  builderGoSettings: BuilderGoSettings | null;
  handleOpenBenefit: (insuranceType: InsuranceType) => void;
  // Bypasses internal title derivation. Use when the caller has already resolved
  // the display title (e.g. custom_module tiles resolved from CDN config in index.tsx).
  overrideTitle?: string;
  cdnPlanDetails?: Record<string, CdnPlanInfo>;
}

export const SupplementalCard = ({
  client,
  employerName,
  insuranceType,
  contentfulSupplementalPlans,
  selectedPlan,
  coveredMembers = {},
  isLimitedPurposeFsa = false,
  supplementalPlans = [],
  recommendedLabel,
  isEducational = false,
  reasonsWhy,
  selectedHealthDeductible,
  dependentsCount,
  surveyAnswers,
  riskAversion,
  contentfulPlan,
  fbsBestMatch = false,
  builderCustomerKey,
  builderGoSettings,
  handleOpenBenefit,
  overrideTitle,
  cdnPlanDetails,
}: SupplementalCardProps) => {
  const { retrieveContentfulData } = useTextContext();

  const isFsaOrFsaMedical = isLimitedPurposeFsa && ['fsa', 'fsa_medical'].includes(insuranceType);

  const supplementalPlanTitle = NO_PLAN_TITLE_OVERRIDE_TYPES.includes(insuranceType)
    ? undefined
    : supplementalPlans
        ?.map((plan) => contentfulSupplementalPlans?.[plan.external_id]?.plan_title || undefined)
        .find(Boolean);

  const contentMapEntry = CONTENT_MAP[insuranceType];

  const cardTitle = (): string => {
    if (isEducational) {
      return contentfulPlan?.card_title || '';
    }

    if (isFsaOrFsaMedical) {
      return retrieveContentfulData<string>(contentMapEntry!.titleAlt, '');
    }

    if (!contentMapEntry) {
      // Prefer Contentful plan_title (locale-aware), fall back to CDN title
      return supplementalPlanTitle ?? overrideTitle ?? '';
    }

    return supplementalPlanTitle || retrieveContentfulData<string>(contentMapEntry.title, '');
  };

  const cardDescription = (): string => {
    if (isEducational) {
      return contentfulPlan?.card_description || '';
    }

    if (isFsaOrFsaMedical) {
      return retrieveContentfulData<string>(contentMapEntry!.descriptionAlt, '');
    }

    if (!contentMapEntry) {
      if (insuranceType.startsWith('custom_module_')) {
        return retrieveContentfulData<string>(
          'benefits_section_additional.other_benefit.custom_module_benefit',
          'Additional supplemental benefit offered by your employer',
        );
      }
      return retrieveContentfulData<string>('benefits_section_additional.other_benefit.card_description', '');
    }

    return retrieveContentfulData<string>(contentMapEntry.description, '');
  };

  const title = cardTitle();
  const description = cardDescription();
  const isEmployerPaid = supplementalPlans.some(
    (i) => contentfulSupplementalPlans?.[i.external_id]?.is_employer_paid,
  );
  const isAutoEnrolled = selectedPlan ? selectedPlan.auto_enrolled : false;
  const planCount = supplementalPlans.length;
  const recommended = recommendedLabel === 'not_recommended' ? null : recommendedLabel;
  const isBest = recommended === 'best';

  const recommendedBadgeTooltip = retrieveContentfulData<string>(
    'benefits_section_additional.tooltips.recommended_badge',
  );

  const bestMatchBadgeTooltip = retrieveContentfulData<string>(
    'benefits_section_additional.tooltips.best_match_badge',
  );

  const renderPlanContent = () => {
    if (selectedPlan) {
      return (
        <SelectedPlanCard
          insuranceType={insuranceType}
          selectedPlan={selectedPlan}
          contentfulSupplementalPlan={contentfulSupplementalPlans?.[selectedPlan.external_id]}
          coveredMembers={coveredMembers}
          handleOpenBenefit={handleOpenBenefit}
          planCount={planCount}
          isAutoEnrolled={isAutoEnrolled}
          isEmployerPaid={isEmployerPaid}
          builderGoSettings={builderGoSettings}
        />
      );
    }

    return (
      <PlanContent
        insuranceType={insuranceType}
        title={title}
        description={description}
        client={client}
        employerName={employerName}
        contentfulSupplementalPlans={contentfulSupplementalPlans}
        planTitle={supplementalPlanTitle ?? overrideTitle}
        handleOpenBenefit={handleOpenBenefit}
        isLimitedPurposeFsa={isLimitedPurposeFsa}
        nonSelectablePlans={supplementalPlans}
        isEducational={isEducational}
        reasonsWhy={reasonsWhy}
        selectedHealthDeductible={selectedHealthDeductible}
        dependentsCount={dependentsCount}
        surveyAnswers={surveyAnswers}
        riskAversion={riskAversion}
        planCount={planCount}
        builderCustomerKey={builderCustomerKey}
        builderGoSettings={builderGoSettings}
        cdnPlanDetails={cdnPlanDetails}
      />
    );
  };

  return (
    <Container
      id={`${insuranceType}-section`}
      data-testid={`${insuranceType}-section`}
      className="benefit-container"
      isAutoEnrolled={isAutoEnrolled}
      recommended={recommended}
      fbsBestMatch={fbsBestMatch}
    >
      {recommended && !isAutoEnrolled && (
        <RecommendedBadge recommended={recommended} fbsBestMatch={fbsBestMatch}>
          <Tooltip
            label={isBest ? bestMatchBadgeTooltip : recommendedBadgeTooltip}
            id={`${insuranceType}-recommended-badge-tooltip`}
          >
            <div className="text">
              <Icon
                size="small"
                weight="fill"
                color={fbsBestMatch && !isBest ? '--text-primary-blue' : '--primary-white'}
                type={fbsBestMatch && isBest ? 'CrownSimple' : 'Star'}
              />
              <H6
                color={fbsBestMatch && !isBest ? '--colors-primary-blue' : '--primary-white'}
                as="div"
                underline
              >
                {isBest ? (
                  <Text field="benefits_section_additional.best_match_badge_label" />
                ) : (
                  <Text field="benefits_section_additional.recommended_badge_label" />
                )}
              </H6>
            </div>
          </Tooltip>
        </RecommendedBadge>
      )}
      <div className="main-content">
        <Header>
          <span className="title">
            <div className="icon">
              <SupplementalIcon type={insuranceType} />
            </div>
            <H4 as="h3" data-testid="supplemental-card-title">
              {title}
            </H4>
          </span>
          {isEmployerPaid && !selectedPlan && (
            <span className="auto-enrolled">
              <Text field="benefits_section.auto_enrolled_info.tagText" />
            </span>
          )}
        </Header>

        {renderPlanContent()}
      </div>
    </Container>
  );
};

export default SupplementalCard;
